import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";
import { fmtEur } from "@/lib/format";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Clock, CheckCircle2, XCircle, RefreshCw,
  Users, Hourglass, AlertTriangle, Trophy, Activity,
} from "lucide-react";

function StatCard({ label, value, sub, icon: Icon, gradient, testid }) {
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${gradient} text-white p-5 shadow-lg`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-mono-fin uppercase tracking-widest text-white/80">{label}</div>
        <Icon size={18} className="text-white/70" />
      </div>
      <div className="font-display font-black text-3xl mt-2 leading-none">{value}</div>
      {sub && <div className="text-[11px] text-white/80 mt-1.5">{sub}</div>}
    </div>
  );
}

export default function AdminMetrics() {
  const { t } = useTranslation();
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/metrics");
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || t("metrics.load_error")); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadMetrics();
    const id = setInterval(loadMetrics, 30000); // refresh every 30s
    return () => clearInterval(id);
  }, [loadMetrics]);

  if (!me) return null;
  if (me.role !== "admin") return <Navigate to="/app" replace />;

  if (loading && !data) {
    return <div className="text-slate-500 p-8 text-center" data-testid="metrics-loading">{t("metrics.loading")}</div>;
  }
  if (!data) return null;

  const { volumes, approval_ratio_30d, avg_review_human, top_clients_30d, daily_series_7d, kyc, generated_at } = data;
  const maxDaily = Math.max(...daily_series_7d.map((d) => d.approved + d.rejected), 1);

  return (
    <div className="space-y-6" data-testid="admin-metrics-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono-fin text-xs uppercase tracking-widest text-purple-600">{t("metrics.eyebrow")}</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-slate-900 mt-1">{t("metrics.title")}</h1>
          <p className="text-slate-500 mt-1">{t("metrics.subtitle")}</p>
        </div>
        <button onClick={loadMetrics} disabled={loading} className="btn-secondary text-sm" data-testid="metrics-refresh">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> {t("metrics.refresh")}
        </button>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label={t("metrics.pending")}
          value={volumes.pending_count}
          sub={t("metrics.in_review", { amount: fmtEur(volumes.pending_amount) })}
          icon={Hourglass}
          gradient="from-orange-500 to-red-500"
          testid="stat-pending"
        />
        <StatCard
          label={t("metrics.approved_24h")}
          value={volumes.approved_24h}
          sub={t("metrics.total_count", { n: volumes.approved_total })}
          icon={CheckCircle2}
          gradient="from-emerald-500 to-green-600"
          testid="stat-approved-24h"
        />
        <StatCard
          label={t("metrics.rejected_24h")}
          value={volumes.rejected_24h}
          sub={t("metrics.total_count", { n: volumes.rejected_total })}
          icon={XCircle}
          gradient="from-rose-500 to-red-600"
          testid="stat-rejected-24h"
        />
        <StatCard
          label={t("metrics.approval_ratio")}
          value={`${approval_ratio_30d}%`}
          sub={approval_ratio_30d >= 80 ? t("metrics.excellent") : approval_ratio_30d >= 60 ? t("metrics.healthy") : t("metrics.review")}
          icon={approval_ratio_30d >= 80 ? TrendingUp : TrendingDown}
          gradient="from-purple-600 to-indigo-600"
          testid="stat-approval-ratio"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard
          label={t("metrics.volume_7d")}
          value={fmtEur(volumes.volume_7d)}
          icon={Activity}
          gradient="from-blue-500 to-cyan-500"
          testid="stat-volume-7d"
        />
        <StatCard
          label={t("metrics.volume_30d")}
          value={fmtEur(volumes.volume_30d)}
          icon={Activity}
          gradient="from-indigo-500 to-purple-500"
          testid="stat-volume-30d"
        />
        <StatCard
          label={t("metrics.avg_review")}
          value={avg_review_human}
          sub={t("metrics.last_100")}
          icon={Clock}
          gradient="from-slate-700 to-slate-900"
          testid="stat-avg-review"
        />
      </div>

      {/* KYC + Top clients */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-3xl bg-white border border-slate-200 p-5" data-testid="kyc-stats">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-purple-600" />
            <h3 className="font-display font-bold text-lg text-slate-900">{t("metrics.kyc_status")}</h3>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-[10px] font-mono-fin uppercase tracking-widest text-slate-500">{t("metrics.total_users")}</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">{kyc.total_users}</div>
            </div>
            <div className="rounded-xl bg-emerald-50 p-3">
              <div className="text-[10px] font-mono-fin uppercase tracking-widest text-emerald-700">{t("metrics.verified")}</div>
              <div className="text-2xl font-bold text-emerald-700 mt-1">{kyc.verified_users}</div>
            </div>
            <div className={`rounded-xl p-3 ${kyc.stale_kyc > 0 ? "bg-red-50" : "bg-slate-50"}`}>
              <div className={`text-[10px] font-mono-fin uppercase tracking-widest ${kyc.stale_kyc > 0 ? "text-red-700" : "text-slate-500"}`}>
                {t("metrics.pending_7d")}
              </div>
              <div className={`text-2xl font-bold mt-1 ${kyc.stale_kyc > 0 ? "text-red-700" : "text-slate-900"}`}>{kyc.stale_kyc}</div>
            </div>
          </div>
          {kyc.stale_kyc > 0 && (
            <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 rounded-xl px-3 py-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              {t("metrics.kyc_warning", { n: kyc.stale_kyc })}
            </div>
          )}
        </div>

        <div className="rounded-3xl bg-white border border-slate-200 p-5" data-testid="top-clients">
          <div className="flex items-center gap-2">
            <Trophy size={18} className="text-amber-500" />
            <h3 className="font-display font-bold text-lg text-slate-900">{t("metrics.top_clients")}</h3>
          </div>
          {top_clients_30d.length === 0 ? (
            <div className="text-sm text-slate-500 mt-4">{t("metrics.no_clients")}</div>
          ) : (
            <ul className="mt-4 space-y-2">
              {top_clients_30d.map((c, i) => (
                <li key={c.user_id} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2 last:border-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center text-xs font-bold shrink-0">
                      {i + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-slate-900 truncate">{c.full_name}</div>
                      <div className="text-[11px] font-mono-fin text-slate-500 truncate">{c.email}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-fin font-bold text-slate-900">{fmtEur(c.total_amount)}</div>
                    <div className="text-[10px] font-mono-fin text-slate-500">{c.tx_count} tx</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Daily series */}
      <div className="rounded-3xl bg-white border border-slate-200 p-5" data-testid="daily-series">
        <div className="flex items-center gap-2">
          <Activity size={18} className="text-blue-600" />
          <h3 className="font-display font-bold text-lg text-slate-900">{t("metrics.daily_title")}</h3>
        </div>
        <div className="mt-5 grid grid-cols-7 gap-3">
          {daily_series_7d.map((d) => {
            const total = d.approved + d.rejected;
            const apprPct = total ? (d.approved / maxDaily) * 100 : 0;
            const rejPct = total ? (d.rejected / maxDaily) * 100 : 0;
            return (
              <div key={d.date} className="flex flex-col items-center gap-2">
                <div className="text-[10px] font-mono-fin text-slate-500">{d.label}</div>
                <div className="w-full h-28 flex flex-col-reverse rounded-xl bg-slate-50 overflow-hidden">
                  <div className="bg-emerald-500 transition-all" style={{ height: `${apprPct}%` }} title={`${d.approved} aprobadas`}></div>
                  <div className="bg-red-500 transition-all" style={{ height: `${rejPct}%` }} title={`${d.rejected} rechazadas`}></div>
                </div>
                <div className="text-[10px] font-mono-fin text-slate-700 font-semibold">{total}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex items-center gap-4 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-500 inline-block"></span> {t("metrics.approved")}</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-500 inline-block"></span> {t("metrics.rejected")}</span>
        </div>
      </div>

      <div className="text-[11px] font-mono-fin text-slate-400 text-right">
        {t("metrics.generated")}: {new Date(generated_at).toLocaleString()} · {t("metrics.subtitle")}
      </div>
    </div>
  );
}
